import { useMutation, UseMutationResult } from "@tanstack/react-query";
import {
  useCallback,
  useState,
  createContext,
  useContext,
  ReactNode,
  useMemo
} from "react";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import { CombinedIncomeSubStep } from "../../constants";
import {
  CombinedIncomeMode,
  CombinedIncomeStepData,
  CreateIncomePayload,
  DocumentType,
  NavigationData
} from "./types";
import { combinedIncomeScreenFlows } from "./constants";
import { resolveFlowTarget, shouldSkipSupervisorDetails } from "./utils";
import {
  type Verification,
  type CombinedIncomeVerificationSchema,
  FieldDisplaySetting,
  IncomeType
} from "shared/types";
import { getErrorMessage } from "utils/error";
import { getByPath } from "shared/utils";

interface CombinedIncomeContextValue {
  mode: CombinedIncomeMode;
  subStep: CombinedIncomeSubStep;
  applicationId: number;
  applicantId: number;
  verification?: Verification;
  stepData: CombinedIncomeStepData;
  verificationSchema?: CombinedIncomeVerificationSchema;
  isFieldRequired: (path: string[]) => boolean;
  isFieldVisible: (path: string[]) => boolean;
  skipSupervisorDetails: boolean;
  skipHousingAssistance: boolean;
  footerSlot?: HTMLElement | null;
  finishVerification: () => void;
  isFinishingVerification: boolean;
  updateStepMutation: UseMutationResult<
    void,
    Error,
    Partial<CombinedIncomeStepData>,
    unknown
  >;
  createIncomeMutation: UseMutationResult<
    { id: number },
    Error,
    CreateIncomePayload,
    unknown
  >;
  refetchVerification: () => void;
  refetchApplication: () => void;
  onNextStep?: () => void;
  getNextStep: (
    currentStep: CombinedIncomeSubStep,
    data?: NavigationData
  ) => CombinedIncomeSubStep | null;
  getBackStep: (
    currentStep: CombinedIncomeSubStep,
    data?: NavigationData
  ) => CombinedIncomeSubStep | null;
}

const CombinedIncomeContext = createContext<
  CombinedIncomeContextValue | undefined
>(undefined);

interface CombinedIncomeProviderProps {
  mode?: CombinedIncomeMode;
  applicationId: number;
  applicantId: number;
  verification?: Verification;
  refetchApplication: () => void;
  refetchVerification: () => void;
  onNextStep?: () => void;
  // Controlled step state (AddNewIncomeSource mode only). When set, step updates
  // go to onStepDataChange instead of being PATCHed to the API.
  stepData?: CombinedIncomeStepData;
  onStepDataChange?: (patch: Partial<CombinedIncomeStepData>) => void;
  // DOM element in the rerun income modal's footer that the step layout portals
  // its buttons into, so they can sit alongside the modal's own buttons.
  footerSlot?: HTMLElement | null;
  children: ReactNode;
}

export const CombinedIncomeProvider = ({
  mode = CombinedIncomeMode.ManualApplication,
  applicationId,
  applicantId,
  verification,
  refetchApplication,
  refetchVerification,
  onNextStep,
  stepData: controlledStepData,
  onStepDataChange,
  footerSlot,
  children
}: CombinedIncomeProviderProps) => {
  const [selectedDocumentType, setSelectedDocumentType] =
    useState<DocumentType | null>(null);

  const isAddingNewIncome = mode === CombinedIncomeMode.AddNewIncomeSource;
  const isControlled = isAddingNewIncome && Boolean(controlledStepData);

  const combinedIncomeStep = useMemo(
    () => verification?.steps?.find((step) => step.name === "combined_income"),
    [verification?.steps]
  );

  const stepData = ((isControlled
    ? controlledStepData
    : combinedIncomeStep?.data) ?? {}) as CombinedIncomeStepData;

  const verificationSchema = verification?.settings as
    | CombinedIncomeVerificationSchema
    | undefined;

  const { isFieldRequired, isFieldVisible } = useMemo(() => {
    const getVerificationFieldSetting = (path: string[]) =>
      getByPath(verificationSchema, [
        stepData.currentIncomeType || IncomeType.Employment,
        "fields",
        ...path
      ]) as FieldDisplaySetting | undefined;

    return {
      isFieldRequired: (path: string[]) =>
        getVerificationFieldSetting(path) === FieldDisplaySetting.Required,
      isFieldVisible: (path: string[]) =>
        getVerificationFieldSetting(path) !== FieldDisplaySetting.Hidden
    };
  }, [verificationSchema, stepData.currentIncomeType]);

  const skipSupervisorDetails = useMemo(
    () =>
      shouldSkipSupervisorDetails(
        verificationSchema,
        stepData.currentIncomeType
      ),
    [verificationSchema, stepData.currentIncomeType]
  );

  const skipHousingAssistance =
    verificationSchema?.housing_assistance_program_enabled === false;

  const subStep =
    stepData.lastStep ??
    (isAddingNewIncome
      ? CombinedIncomeSubStep.SelectIncomeType
      : CombinedIncomeSubStep.IncomeStatus);

  const updateStepMutation = useMutation({
    mutationKey: [
      "UPDATE_COMBINED_INCOME_STEP",
      mode,
      applicationId,
      verification?.id
    ],
    mutationFn: async (data: Partial<CombinedIncomeStepData>) => {
      if (isControlled) {
        onStepDataChange?.(data);
        return;
      }

      await instance.patch(
        `/portal/v1/applications/${applicationId}/verifications/${verification?.id}/steps/combined_income?applicant_id=${applicantId}`,
        { data }
      );
    },
    onSuccess: () => {
      if (isAddingNewIncome) return;

      refetchVerification();
    }
  });

  const createIncomeMutation = useMutation({
    mutationKey: ["CREATE_INCOME", applicationId, verification?.id],
    mutationFn: (data: CreateIncomePayload) => {
      if (isAddingNewIncome) return Promise.resolve({ id: -1 });

      return instance
        .post(
          `/portal/v1/applications/${applicationId}/verifications/${verification?.id}/income?applicant_id=${applicantId}`,
          data
        )
        .then((res) => res.data);
    },
    onSuccess: () => {
      refetchVerification();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) || "Failed to create income");
    }
  });

  const finishVerificationMutation = useMutation({
    mutationFn: async () => {
      if (isAddingNewIncome) return;

      await instance.post(
        `/portal/v1/applications/${applicationId}/verifications/${verification?.id}/finish?applicant_id=${applicantId}`
      );
    },
    onSuccess: () => {
      if (isAddingNewIncome) return;

      updateStepMutation.mutate(
        { lastStep: CombinedIncomeSubStep.Completed },
        {
          onSuccess: () => {
            refetchVerification();
            onNextStep?.();
          },
          onError: (error) => {
            toast.error(
              getErrorMessage(error) || "Failed to complete combined income"
            );
          }
        }
      );
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) || "Failed to complete verification");
    }
  });

  const finishVerification = useCallback(
    () => finishVerificationMutation.mutate(),
    [finishVerificationMutation]
  );
  const isFinishingVerification = finishVerificationMutation.isPending;

  const getNextStep = useCallback(
    (
      currentStep: CombinedIncomeSubStep,
      data?: NavigationData
    ): CombinedIncomeSubStep | null =>
      resolveFlowTarget(combinedIncomeScreenFlows[currentStep].next, data),
    []
  );

  const getBackStep = useCallback(
    (
      currentStep: CombinedIncomeSubStep,
      data?: NavigationData
    ): CombinedIncomeSubStep | null =>
      resolveFlowTarget(combinedIncomeScreenFlows[currentStep].back, data),
    []
  );

  const value: CombinedIncomeContextValue = useMemo(
    () => ({
      mode,
      subStep,
      footerSlot,
      applicationId,
      applicantId,
      verification,
      stepData,
      verificationSchema,
      isFieldRequired,
      isFieldVisible,
      skipSupervisorDetails,
      skipHousingAssistance,
      finishVerification,
      isFinishingVerification,
      updateStepMutation,
      createIncomeMutation,
      refetchVerification,
      refetchApplication,
      onNextStep,
      selectedDocumentType,
      setSelectedDocumentType,
      getNextStep,
      getBackStep
    }),
    [
      mode,
      subStep,
      footerSlot,
      applicationId,
      applicantId,
      verification,
      stepData,
      verificationSchema,
      isFieldRequired,
      isFieldVisible,
      skipSupervisorDetails,
      skipHousingAssistance,
      finishVerification,
      finishVerificationMutation,
      updateStepMutation,
      createIncomeMutation,
      refetchVerification,
      refetchApplication,
      onNextStep,
      selectedDocumentType,
      getNextStep,
      getBackStep
    ]
  );

  return (
    <CombinedIncomeContext.Provider value={value}>
      {children}
    </CombinedIncomeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCombinedIncome = () => {
  const context = useContext(CombinedIncomeContext);
  if (!context)
    throw new Error(
      "useCombinedIncome must be used within CombinedIncomeProvider"
    );
  return context;
};
